import { Avatar, Divider, Grid, Typography } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { LuCopy, LuPencil } from 'react-icons/lu';
import { ICandidate, amplitude, updateCandidateSubmission } from 'wisestep-api';
import { WsButton, WsColors, WsTypoColors, WsTypography, WsTypoVariants } from 'wisestep-base';

import AI_Image from '../../../../../images/AI.svg';
import { failed, success } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { copyStringToClipboard } from '../../childComponets/CandidateDetailsCard/Helper';

import CandidateSummaryDialog from './CandidateSummaryDialog';

interface CandidateSummaryProps {
  submission: ICandidate;
  jobDescription: string;
}

const CandidateSummary = ({ submission, jobDescription }: CandidateSummaryProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const copy = () => {
    amplitude('Copy_Candidate_Summary', {
      clickedOn: 'Copy Button',
      pageName: 'candidate-layout',
      pageUrl: window.location.href,
    });
    copyStringToClipboard(submission?.candidateJobSummary ?? '');
    setNotification(success('Copied Successfully !!'));
  };
  const handleToggleDialog = () => setOpenDialog((prev) => !prev);

  const handleSaveData = async (summary: string) => {
    try {
      amplitude('Save_Candidate_Summary', {
        clickedOn: 'Save Button',
        pageName: 'candidate-layout',
        pageUrl: window.location.href,
      });
      await updateCandidateSubmission([
        {
          candidateJobSummary: summary ?? '',
          id: submission.id,
          submissionEmailBody: !isEmpty(summary)
            ? submission?.submissionEmailBody?.replaceAll('{{candidateSummary}}', summary)
            : submission?.submissionEmailBody,
        } as ICandidate,
      ]);
      setNotification(success('Candidate Summary successfully saved'));
      window.location.reload();
    } catch (e) {
      setNotification(failed('!Something Went Wrong.Try again'));
      console.log(e);
    } finally {
      handleToggleDialog();
    }
  };
  return (
    <Grid
      display="flex"
      direction="column"
      border={`1.5px solid ${WsColors.NEUTRAL_100}`}
      rowGap={1}
    >
      <Grid display="flex" alignItems="center" justifyContent="space-between" p={1}>
        <Grid display="flex" alignItems="center" columnGap={1.5}>
          <Avatar src={AI_Image} variant="square" />
          <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
            Candidate Summary
          </WsTypography>
          {!submission.candidateJobSummary && (
            <WsTypography color={WsTypoColors.TERTIARY}>Summary not available</WsTypography>
          )}
        </Grid>
        <Grid display="flex" justifyItems="flex-end" columnGap={1.5}>
          {!submission.candidateJobSummary ? (
            <>
              <WsButton size="small" variant="text" disabled>
                Add Summary
              </WsButton>
              <WsButton size="small" variant="text" onClick={handleToggleDialog}>
                Generate Summary
              </WsButton>
            </>
          ) : (
            <>
              <WsButton
                size="small"
                variant="outlined"
                startIcon={<LuCopy color={WsColors.TheBlues500} />}
                onClick={copy}
              >
                Copy
              </WsButton>
              <WsButton size="small" startIcon={<LuPencil />} disabled>
                Edit
              </WsButton>
            </>
          )}
        </Grid>
      </Grid>
      {submission?.candidateJobSummary && (
        <>
          <Divider sx={{ borderColor: WsColors.NEUTRAL_100 }} />
          <Grid alignItems="center" px={2}>
            <Typography
              dangerouslySetInnerHTML={{
                __html: submission?.candidateJobSummary ?? '',
              }}
            />
          </Grid>
        </>
      )}
      {openDialog && (
        <CandidateSummaryDialog
          handleClose={handleToggleDialog}
          handleOnSave={(data) => handleSaveData(data)}
          jobDescription={jobDescription ?? ''}
          resumeLocation={submission?.resumeLocation ?? ''}
          existedSummary={submission?.candidateJobSummary ?? ''}
        />
      )}
    </Grid>
  );
};

export default CandidateSummary;
